import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, FileCheck, Banknote, Heart, Phone, ArrowRight } from "lucide-react";
import { SITE, IMAGES } from "@/lib/site";
import SectionTitle from "@/components/SectionTitle";
import PriceBanner from "@/components/PriceBanner";
import usePageMeta from "@/hooks/usePageMeta";

const DOSSIERS = [
  { icon: FileCheck, title: "Sécurité sociale", desc: "Demande de capital décès auprès de la CPAM, justificatifs et formulaires : nous montons le dossier complet." },
  { icon: Banknote, title: "Mutuelles", desc: "Activation des garanties prévoyance, prise en charge des frais funéraires : nous transmettons les pièces justificatives." },
  { icon: ShieldCheck, title: "Assurances vie & décès", desc: "Identification des contrats, mise en relation avec les assureurs, suivi du déblocage des fonds." },
  { icon: Heart, title: "Aide au montage des dossiers", desc: "Banque, employeur, retraite, allocations : nous vous orientons vers chaque organisme à contacter." },
];

const PREVOYANCE_BENEFITS = [
  "Soulager vos proches d'un fardeau financier et administratif",
  "Choisir vous-même chaque détail de la cérémonie",
  "Figer le tarif aujourd'hui — protégé contre l'inflation",
  "Capital protégé et restitué intégralement aux familles",
];

export default function PrevoyancePage() {
  usePageMeta(
    "Prévoyance & Capital décès — Baho",
    "Contrats obsèques, accompagnement capital décès, sécurité sociale, mutuelles, assurances. Libérez rapidement les fonds avec Pompes Funèbres Lumière des Nations à Baho."
  );
  return (
    <div data-testid="prevoyance-page">
      <section className="relative bg-black text-white py-28 lg:py-40">
        <div className="absolute inset-0 opacity-30">
          <img src={IMAGES.sky} alt="" className="w-full h-full object-cover grayscale" />
        </div>
        <div className="relative container-soft">
          <p className="overline text-white/60">Prévoyance & Capital décès</p>
          <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl mt-4 max-w-4xl tracking-tight leading-[1.02]">
            Libérer les fonds, <em className="italic font-light">vite et sereinement</em>.
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-lg leading-relaxed">
            Sécurité sociale, mutuelles, assurances : nous montons les dossiers à votre place
            et accélérons le déblocage des capitaux pour que vous puissiez avancer.
          </p>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="container-soft">
          <SectionTitle
            overline="Capital décès — accompagnement complet"
            title="Quatre dossiers, un seul interlocuteur : nous."
            kicker="Nos équipes connaissent les organismes, les délais et les pièges. Vous gagnez du temps et de la sérénité."
          />
          <div className="grid md:grid-cols-2 gap-px bg-black/10 mt-16 border border-black/10">
            {DOSSIERS.map((d, i) => {
              const Icon = d.icon;
              return (
                <article
                  key={i}
                  data-testid={`dossier-${i}`}
                  className="bg-white p-10 lg:p-12 hover:bg-neutral-50 transition-colors duration-500"
                >
                  <Icon size={28} strokeWidth={1.25} />
                  <h3 className="font-serif-display text-2xl lg:text-3xl mt-6">{d.title}</h3>
                  <p className="mt-4 text-neutral-600 leading-relaxed text-lg">{d.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-neutral-50 py-24 lg:py-32 border-y border-black/10">
        <div className="container-soft grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="overline">Contrat obsèques</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">
              Anticiper, c'est protéger ceux qu'on aime.
            </h2>
            <p className="mt-6 text-neutral-700 text-lg leading-relaxed">
              Souscrire un contrat obsèques, c'est choisir aujourd'hui ce qui sera fait demain :
              cérémonie, lieu, intervenants, budget. C'est aussi alléger considérablement
              le poids émotionnel et financier qui pèsera sur vos proches.
            </p>
            <ul className="mt-10 space-y-4">
              {PREVOYANCE_BENEFITS.map((b, i) => (
                <li key={i} className="flex items-start gap-4 pb-4 border-b border-black/10">
                  <span className="font-serif-display text-2xl text-black/40 leading-none mt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-neutral-800 leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <img src={IMAGES.hands} alt="Mains accompagnement" className="w-full aspect-[4/5] object-cover grayscale" loading="lazy" />
          </div>
        </div>
      </section>

      <PriceBanner />

      <section className="py-24 lg:py-32 text-center">
        <div className="container-soft">
          <SectionTitle
            overline="Un rendez-vous sans engagement"
            title="Faisons le point ensemble, à votre rythme."
            kicker="À domicile ou en agence, un conseiller vous présente toutes les options, sans jargon ni pression."
            align="center"
          />
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a href={SITE.phoneHref} data-testid="prevoyance-cta-call" className="inline-flex items-center justify-center gap-3 bg-black text-white px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-neutral-800 transition-all duration-300">
              <Phone size={18} strokeWidth={1.5} /> {SITE.phone}
            </a>
            <Link to="/contact" data-testid="prevoyance-cta-contact" className="inline-flex items-center justify-center gap-2 border border-black px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-black hover:text-white transition-all duration-300">
              Prendre rendez-vous <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
